import * as React from "react";
import NextImage from "next/image";
import { Box, Heading, Stack, Text, StackProps } from "@chakra-ui/react";

import Button from "components/Chakra/Button";
import CustomWrapper from "components/Chakra/CustomWrapper";
import CityHeader from "components/CityHeader";
import Referral from "components/Referral";
import UserBadge from "components/Badges";
import { getAllURLWithoutSpecificQueryParam } from "helpers/global";
import CustomLink from "components/Chakra/CustomLink";
import { useLoginModal } from "modals/useLoginModal";
import { useAlertModal } from "modals/useAlertModal";
import IconCheck from "components/Chakra/Icons/Check";
import IconCrown from "components/Chakra/Icons/Crown";
import IconDottedArrow from "components/Chakra/Icons/DottedArrow";
import useRouter from "hooks/useRouter";
import useGlobalState from "hooks/useGlobalState";
import useHelperCRM from "hooks/useHelperCRM";
import useScrollToId from "hooks/useScrollToId";
import useViewport from "hooks/useViewport";
import { EOrderTypeId } from "utils/payment/types";

const ActiveBtn: React.FC = () => {
  const { user } = useGlobalState();
  const router = useRouter();
  const { openModal: openLoginModal } = useLoginModal();
  const [hasPremium, setHasPremium] = React.useState(false);

  const setRedirectUrlToSession = (): void => {
    const url = router.query.platba
      ? getAllURLWithoutSpecificQueryParam("platba", router)
      : window.location.href;
    sessionStorage.setItem("REDIRECT_PAGE_BEFORE_PAYMENT", url);
  };

  const triggerLoginAndPayment = (): void =>
    openLoginModal({
      onSuccessfulLogin: () => {
        setRedirectUrlToSession();
        router.navigateTo("/platba/premiovy-ucet");
      },
      loginEvent: {
        LOGIN: {
          "dl.eAct": "login",
          "dl.eLab": "Premium – landing premium",
          "dl.eCat": "",
        },
        REGISTRATION: {
          "dl.eAct": "registration",
          "dl.eLab": "Premium – landing premium",
          "dl.eCat": "",
        },
      },
    });

  React.useEffect(() => {
    if (user?.premiumTill) {
      setHasPremium(true);
    }
  }, [user]);

  if (hasPremium) {
    return (
      <Stack w="fit-content">
        <CustomLink variant="button" to="/nastaveni">
          <Stack direction="row" align="center">
            <IconCheck fillWith="ud-white" />
            <Text color="ud-white">Premium aktivní</Text>
          </Stack>
        </CustomLink>
      </Stack>
    );
  }

  return (
    <>
      {user ? (
        <CustomLink
          variant="button"
          trackEventPayload={{
            "dl.eCat": "landing",
            "dl.eAct": "premium.clicked",
            "dl.eLab": "Premium lander: Aktivovat premium",
          }}
          to="/platba/premiovy-ucet"
          onClick={setRedirectUrlToSession}
        >
          Aktivovat Premium
        </CustomLink>
      ) : (
        <Button
          trackEventPayload={{
            "dl.eCat": "landing",
            "dl.eAct": "premium.clicked",
            "dl.eLab": "Premium lander: Aktivovat premium",
          }}
          onClick={triggerLoginAndPayment}
        >
          Aktivovat Premium
        </Button>
      )}
    </>
  );
};

export const Item: React.FC<{
  image?: string;
  title: string;
  text: string;
  bottomPad?: string;
  picAlignment?: StackProps["align"];
  icon?: React.ReactNode;
}> = ({
  image,
  title,
  text,
  bottomPad = "40px",
  picAlignment = "center",
  icon,
}) => {
  const { isMobile } = useViewport();

  let img = null;
  if (icon) {
    img = <Box w="32px">{icon}</Box>;
  } else if (image) {
    img = <NextImage priority src={image} alt={title} width="80" height="80" />;
  }

  return (
    <Stack
      w={!isMobile ? "268px" : "100%"}
      align={!isMobile ? "center" : picAlignment}
      spacing={icon ? "16px" : "30px"}
      px="20px"
      direction={!isMobile ? "column" : "row"}
      pb={isMobile ? bottomPad : undefined}
      position="relative"
    >
      {img}
      <Stack spacing="4px">
        <Heading
          as="h3"
          textAlign={!isMobile ? "center" : "start"}
          variant="h3_1"
        >
          {title}
        </Heading>
        <Text textAlign={!isMobile ? "center" : "start"} variant="small_2">
          {text}
        </Text>
      </Stack>
    </Stack>
  );
};

const PremiumLanding: React.FC = ({}) => {
  const { user } = useGlobalState();
  const { isDesktop, isMobile } = useViewport();
  const { scrollToId } = useScrollToId();
  const { query } = useRouter();
  const { openModal: openAlertModal } = useAlertModal();
  const { updateUserAfterPayment } = useHelperCRM();

  React.useEffect(() => {
    if (!user) {
      return;
    }
    if (query.platba) {
      if (query.platba === "nezaplacena") {
        return openAlertModal({
          animation: "error",
          title: "Premium účet se nepodařilo aktivovat",
          children:
            "Platbu se bohužel nepodařilo uskutečnit. Zkuste to prosím znovu, nebo nás kontaktujte na info@ulovdomov.cz.",
          submitText: "Zkusit znovu",
        });
      }

      const orderType = query.orderType;
      const isPremiumPayment =
        !orderType ||
        [
          EOrderTypeId.PREMIUM_ACC_6_MONTHS,
          EOrderTypeId.PREMIUM_ACC_12_MONTHS,
          EOrderTypeId.PREMIUM_ACC_UNLIMITED,
        ].includes(Number(orderType));

      if (!isPremiumPayment) {
        return;
      }

      openAlertModal({
        animation: "check",
        title: "Stali jste se prémiovým uživatelem",
        children:
          "Využijte prémiové výhody naplno. Hledejte pronájmy bez provize nebo si nastavte instantního Hlídacího psa.",
        submitText: "Paráda",
      });
      updateUserAfterPayment({
        user,
        orderType: query.orderType,
      });
    }
  }, [user]);

  return (
    <>
      <CityHeader>
        <IconCrown fillWith="ud-primary" boxSize="29px" />
        <Heading as="h1" color="ud-black" pt="20px" pb="20px" lineHeight="46px">
          Najděte bydlení lépe a rychleji
        </Heading>
        <Text textAlign="center" pb="40px" as="h2">
          S Premium účtem za <b>199 Kč</b> ušetříte čas a zvýšíte své šance.
        </Text>
        <Box pb={isDesktop ? "152px" : "73px"}>
          <ActiveBtn />
        </Box>
        <IconDottedArrow fillWith="ud-primary" boxSize="35px" />
      </CityHeader>
      <CustomWrapper>
        <Stack
          direction="row"
          mt="60px"
          mb="40px"
          wrap="wrap"
          justify="center"
          spacing="0px"
        >
          <Item
            image="/img/icons/raketa.svg"
            title="Na rychlosti záleží"
            text="Buďte u nabídek dříve než ostatní"
          />
          <Item
            image="/img/icons/exekuce.svg"
            title="Přednostní kontaktování"
            text="Oslovte inzeráty, které jsou pro ostatní zamčené"
          />
          <Item
            image="/img/icons/pocitac.svg"
            title="Portál bez reklam"
            text="Vyhledávejte komfortněji a bez vyrušení"
            bottomPad="0px"
          />
        </Stack>
        <Stack mt="40px" align="center" mb="80px">
          <Button
            w="fit-content"
            variant="outline"
            onClick={() => scrollToId("morePremium")}
          >
            Více o výhodách
          </Button>
        </Stack>
        <Referral
          text={[
            "Kvalitní nabídky během sezóny mizely dost rychle a pronajímatelé ne vždy reagovali na naše poptávky.",
            "Aktivovali jsem si proto Premium účet, vyplnili jsme vizitku a nastavili jsme si přednostního Hlídacího psa. Pak už stačilo jen pár dní a našli jsme bydlení přesně podle našich představ",
          ]}
          name="Radek a Petra"
          relationship="nájemníci"
        />
        <Box mb="94px" />
      </CustomWrapper>
      <CustomWrapper bgColor="ud-grey-100">
        <Stack
          mt={isDesktop ? "100px" : "60px"}
          direction={isDesktop ? "row" : "column"}
          justify="space-around"
          align="center"
          mb={isDesktop ? "100px" : "60px"}
        >
          <Box w={isDesktop ? "340px" : "275px"}>
            <Heading
              as="h2"
              mb="20px"
              textAlign={isDesktop ? "start" : "center"}
            >
              Vynikněte mezi
              <br />
              zájemci
            </Heading>
            <Text
              variant="small_2"
              mb={isDesktop ? "40px" : "0px"}
              textAlign={isDesktop ? "start" : "center"}
            >
              Nejlepší nabídky mají desítky zájemců v řádu hodin. Premium účet
              a kvalitně vyplněný profil vám pomůžou zaujmout pronajímatele či
              makléře a zvýší tak vaši šanci získat vysněné bydlení.
            </Text>
            {isDesktop && <ActiveBtn />}
          </Box>
          <Box pt="75px">
            <Stack
              w="247px"
              minH="150px"
              borderRadius="6px"
              boxShadow="md"
              bgColor="white"
              position="relative"
              align="center"
              spacing="0px"
            >
              <Text
                variant="large_2"
                fontWeight="bold"
                color="ud-primary"
                pt="47px"
                textAlign="center"
              >
                {!user?.firstName && !user?.lastName
                  ? "Marek Lovec"
                  : user.firstName + " " + user.lastName}
              </Text>
              <Text variant="small_2" pb="8px">
                {user?.email || "marek.lovec@email.cz"}
              </Text>
              <UserBadge type="premium" />
              <Box pt="33px" />
              <Box position="absolute" top="-50px">
                <NextImage
                  src={user?.photoPath || "/img/scenes/PremiumLanding/man.jpg"}
                  alt={
                    user?.firstName && user.lastName
                      ? `${user.firstName} ${user.lastName}`
                      : "Uživatel"
                  }
                  width="90"
                  height="90"
                  style={{
                    borderRadius: "100%",
                  }}
                />
              </Box>
            </Stack>
            {!isDesktop && (
              <Stack align="center" mt="40px">
                <ActiveBtn />
              </Stack>
            )}
          </Box>
        </Stack>
      </CustomWrapper>
      <CustomWrapper id="morePremium">
        <Stack align="center">
          <Heading
            as="h2"
            mt={isMobile ? "60px" : "80px"}
            textAlign="center"
            w={isMobile ? "100%" : "412px"}
          >
            Jeden účet, spousta výhod – za méně než 1 Kč denně
          </Heading>
        </Stack>
        <Stack
          direction="row"
          mt="60px"
          mb={isMobile ? "0px" : "100px"}
          wrap="wrap"
          justify="center"
          spacing={isMobile ? "0px" : "15px"}
        >
          <Item
            picAlignment="start"
            image="/img/icons/raketa.svg"
            title="Přednostní Hlídací pes"
            text="Večer už můžou být nejlepší nabídky pryč. Vy upozornění na nové inzeráty dostanete ihned po zveřejnění nabídky – na rozdíl od ostatních zájemců, které pes upozorní s denním zpožděním."
          />
          <Item
            picAlignment="start"
            image="/img/icons/exekuce.svg"
            title="Okamžité kontaktování"
            text="Získáte možnost vidět neomezeně telefonní číslo a ihned oslovit majitele. Navíc se vám odemknou inzeráty bez provize o 72 hodin dříve než ostatním."
          />
          <Item
            picAlignment="start"
            image="/img/icons/pocitac.svg"
            title="Žádné reklamy"
            text="Co víc dodat – díky Premium účtu si navíc můžete užít komfortnější
            a přehlednější vyhledávání bez reklam."
            bottomPad="0px"
          />
        </Stack>
        <Stack mt="35px" mb="80px" align="center">
          <ActiveBtn />
        </Stack>
      </CustomWrapper>
    </>
  );
};

export default PremiumLanding;
